<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">Штатный персонал</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-btn v-if="canEditStaffs()" class="custom-btn-primary" :disabled="isBusy" @click="onCreate()">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новый сотрудник
        </b-btn>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12">
            <label class="small text-muted">Организация</label>
            <organization-select
              id="input-organization-select"
              ref="organization_select"
              :value="filters.organization_id"
              @onSelect="onSelectOrganization"
            />
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="staffs"
            ref="staffs"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-dblclicked="onRowClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="6"/>
            </template>

            <template #cell(name)="row">
              {{ getShortName(row.item.surname, row.item.name, row.item.patronymic) }}
            </template>

            <template #cell(contract_add_date)="row">
              {{formatTableDate(row.item.contract_add_date)}}
            </template>

            <template #cell(patent_issue_date)="row">
              {{formatTableDate(row.item.patent_issue_date)}}
            </template>

            <template #cell(patent_expiration)="row">
              {{formatTableDate(row.item.patent_expiration)}}
            </template>

            <template #cell(active)="row">
              <v-icon name="buttons/cross" v-if="!row.item.active" :size="13"/>
              <v-icon name="buttons/check" v-if="row.item.active" :size="14"/>
            </template>

            <template #cell(action)="row">
              <b-btn variant="link" class="text-dark" @click="onEdit(row.item)">
                <v-icon name="buttons/edit" :size="15"/>
              </b-btn>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          />
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных сотрудников.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте нового сотрудника прямо сейчас</p>
          <b-btn v-if="canEditStaffs()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="onCreate()">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новый сотрудник
          </b-btn>
        </div>
      </div>
    </div>

    <dynamic-tables
      storage-key="staffs_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />
  </div>
</template>

<script>

import staff from "../../network/staff";
  import helpers from "../../utilites/helpers";
  import dateFormatter from "../../utilites/dateFormatter";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
 import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
    import OrganizationSelect from "../Organizations/OrganizationSelect.vue";

export default {
  name: "Staffs",
  components: { TablesSkeleton, PaginationFooter, FiltersDrawer, DynamicTables, OrganizationSelect },
  mixins: [
    staff,
    helpers, dateFormatter, permissions, pageSearchService
  ],
  data() {
    return {
      items: [],
      fields: [
        {
          key: 'id',
          label: 'ID',
          thStyle: { width: '40px', 'min-width': '40px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'name',
          label: "ФИО",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'citizenship',
          label: "Гражданство",
          thStyle: { width: '90px', 'min-width': '90px' },
          sortable: false,
          visible: true,
        },
        {
          key: 'status',
          label: "Статус",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: false,
          visible: true,
        },
        {
          key: 'organization_name',
          label: "Организация",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: false,
          visible: true,
        },
        {
          key: 'job_title_name',
          label: "Должность",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: false,
          visible: true,
        },
        {
          key: 'contract_add_date',
          label: "Дата приёма",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'inn',
          label: "ИНН",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: false,
          visible: true,
        },
        {
          key: 'snils',
          label: "СНИЛС",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: false,
          visible: true,
        },
        {
          key: 'patent_number',
          label: "Патент / ВНЖ",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: false,
          visible: true,
        },
        {
          key: 'patent_issue_date',
          label: "Дата выдачи патента",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: false,
          visible: true,
        },
        {
          key: 'patent_expiration',
          label: "Дата окончания патента",
          thStyle: { width: '100px', 'min-width': '100px' },
          sortable: true,
          visible: true,
        },
        {
          key: 'active',
          label: "Активный",
          class: 'text-center',
          thStyle: { width: '50px', 'min-width': '50px' },
          sortable: false,
          visible: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: "Действия",
        thStyle: { width: '80px', 'min-width': '80px' },
      },
      isBusy: false,
      sortBy: 'id',
      sortDesc: true,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
        name: ''
      },
    }
  },
  async created() {
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.organization_id > 0) {
        this.filters.organization_id = query.organization_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.getItems();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      const allFields = [
        { ...this.serviceStartField, sticky: 'left' },
        ...this.visibleFields,
        { ...this.serviceEndField, sticky: 'right' }
      ];
      return this.handleFixedCols(allFields);
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.organization_id !== defaults.organization_id) {
        tags.push({ key: 'organization', label: `Организация`});
      }

      return tags;
    },
    isFiltered() {
      const hasQuery = !!this.filters.query && this.filters.query.trim().length > 0;
      const hasTags = this.activeFilterTags.length > 0;

      return hasQuery || hasTags;
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        organization_id: this.filters.organization_id,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getStaffs(data);
      this.isBusy = false;

      if (result.data == null) {
        this.items = [];
        this.totalRows = 0;
        const mess = "Что-то пошло не так. Попробуйте обновить страницу и повторить действие."
        this.showError(mess);
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;

      this.items.forEach(function (item) {
        let variants = {};
        let today = new Date().toISOString().slice(0, 10);
        let plusTwoWeeks = new Date();
          plusTwoWeeks.setDate(plusTwoWeeks.getDate() + 14);
        let twoWeeks = new Date(plusTwoWeeks).toISOString().slice(0, 10);

        if (item.patent_expiration < today) {
          variants.patent_expiration = "danger";
        } else if (item.patent_expiration < twoWeeks) {
          variants.patent_expiration = "warning";
        } else {
          variants.patent_expiration = "null";
        }

        item._cellVariants = variants;
      }.bind(this));
    },
    onCreate() {
      if (this.hasPermission([PERM.STAFF_EDIT])) {
        this.$router.push({name: 'StaffAdd'}).catch(()=>{});
      }
    },
    onEdit(item) {
      if (this.hasPermission([PERM.STAFF_EDIT])) {
        this.$router.push({name: 'StaffEdit', params: { id: item.id }}).catch(()=>{});
      }
    },
    // Filters
    removeFilterTag(tag) {
      if (tag.key === 'organization') {
        this.$refs.organization_select.onClear();
        this.filters.organization_id = 0;
      }
      this.onSearch();
    },
    getDefaultFilters() {
      return {
        query: '',
        organization_id: 0,
      }
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
        return;
      }

      this.filters = filters;
      this.$refs.organization_select.onClear();

      this.onSearch();
    },
    onSelectOrganization(id) {
      this.filters.organization_id = id;
    },
    onRowClicked(item) {
      this.onEdit(item);
    },
    getShortName(surname, name, patronymic) {
      const firstCharName = name.slice(0,1);
      const firstCharPatronymic = patronymic.slice(0,1);
      if (firstCharPatronymic === '') {
        return `${surname} ${firstCharName}.`;
      }
      return `${surname} ${firstCharName}. ${firstCharPatronymic}.`;
    },
    // Permissions
    canEditStaffs() {
      return this.hasPermission([PERM.STAFF_EDIT]);
    }
  }
}
</script>

<style>
#staffs td {
  vertical-align: middle !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Staff/Staffs.vue